import { useAlert } from "contexts/alertContext";
import { handleError } from "helpers/utils";
import { useState, useCallback, useEffect } from "react";
import { useQueryClient } from "react-query";

type ApiResponse<T> = {
  message: string;
  data: T;
};

type FetchSectionProps<T, U> = {
  section: string;
  fetchData: () => Promise<ApiResponse<T>>;
  fetchPermission: () => Promise<U>;
  processResponse?: (data: T) => T;
  onSuccess?: (data: T) => void;
};

const useFetchSection = <T, U>({
  section,
  fetchData,
  fetchPermission,
  processResponse = (data: T) => data,
  onSuccess,
}: FetchSectionProps<T, U>) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<T | null>(null);
  const [permission, setPermission] = useState<U | null>(null);
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const { setAlert } = useAlert();

  const fetchSection = useCallback(async () => {
    try {
      const [dataResponse, permissionResponse] = await Promise.all([
        queryClient.fetchQuery(`get ${section}`, fetchData),
        fetchPermission(),
      ]);

      const processedData = processResponse(dataResponse.data);

      setData(processedData);
      if (onSuccess) {
        onSuccess(processedData);
      }

      setPermission(permissionResponse);
    } catch (error: any) {
      setError(error.message || "Something went wrong");
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [queryClient, fetchData, fetchPermission, processResponse, onSuccess, setAlert, section]);


  useEffect(() => {
    fetchSection();
  }, [section]);

  return { loading, data, error, permission, fetchSection, revalidate: fetchSection };
};

export default useFetchSection;
